import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useCatalog } from "@/lib/catalog";
import { Reveal } from "@/components/Reveal";
import { DynIcon } from "@/components/DynIcon";
import { Eyebrow } from "@/components/Ui";
import { ArrowRight, CheckCircle2 } from "lucide-react";

// Routes where the user is already converting: no extra CTA there.
const NO_CTA_ROUTES = ["/demo", "/contact"];

function useTrialDays() {
  const { catalog } = useCatalog();
  return catalog?.plans?.find((p) => p.trial_days)?.trial_days;
}

/** Reassurance checks shown right under the hero CTAs. */
export function TrustChecks({ light = false, testid = "trust-checks" }) {
  const { t } = useLang();
  const trial = useTrialDays();
  const items = [
    trial ? t("cta.trustTrial", { n: trial }) : null,
    ...t("cta.trustItems"),
  ].filter(Boolean);
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-2" data-testid={testid}>
      {items.map((label) => (
        <li
          key={label}
          className={`flex items-center gap-1.5 text-sm font-medium ${light ? "text-slate-200" : "text-slate-600"}`}
        >
          <CheckCircle2 className="h-4 w-4 shrink-0 text-gold" />
          {label}
        </li>
      ))}
    </ul>
  );
}

/** Outcome-led benefits band (what the customer gains). */
export function BenefitsBand() {
  const { t } = useLang();
  const items = t("cta.benefits");
  return (
    <section className="border-y border-slate-200 bg-white" data-testid="benefits-section">
      <div className="mx-auto grid max-w-7xl divide-y divide-slate-200 px-4 sm:px-6 md:grid-cols-3 md:divide-x md:divide-y-0">
        {items.map((b, i) => (
          <Reveal key={b.title} delay={i * 100} className="min-w-0">
            <div className="flex h-full items-start gap-4 py-8 md:px-6" data-testid={`benefit-${i}`}>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold">
                <DynIcon name={b.icon} className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-display text-base font-bold text-marine">{b.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">{b.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/** Final call-to-action banner, rendered before the footer on every page except demo/contact. */
export function FinalCta() {
  const { t } = useLang();
  const { pathname } = useLocation();
  if (NO_CTA_ROUTES.includes(pathname)) return null;
  return (
    <section className="bg-mist px-4 py-16 sm:px-6" data-testid="final-cta-section">
      <Reveal className="mx-auto max-w-7xl">
        <div className="relative overflow-hidden rounded-2xl bg-marine px-6 py-12 sm:px-12 lg:py-16">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-gold/20 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-brand/30 blur-3xl" aria-hidden="true" />
          <div className="relative grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_auto]">
            <div className="min-w-0">
              <Eyebrow light>{t("cta.finalEyebrow")}</Eyebrow>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight text-white sm:text-4xl">
                {t("cta.finalTitle")}
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">{t("cta.finalSub")}</p>
              <div className="mt-6">
                <TrustChecks light testid="final-cta-trust" />
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Link to="/demo" className="btn-gold px-6 py-3.5 text-base" data-testid="final-cta-demo-button">
                {t("cta.finalPrimary")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/tarifs" className="btn-outline-light px-6 py-3.5 text-base" data-testid="final-cta-pricing-button">
                {t("cta.finalSecondary")}
              </Link>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/** Slim sticky CTA bar for mobile/tablet, shown after the user scrolls past the hero. */
export function MobileStickyCta() {
  const { t } = useLang();
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);
  if (NO_CTA_ROUTES.includes(pathname)) return null;
  return (
    <>
      {/* spacer so the fixed bar never hides the end of the footer */}
      <div className="h-[72px] bg-marine xl:hidden" aria-hidden="true" />
      <div
        className={`fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-xl transition-transform duration-300 xl:hidden ${
          show ? "translate-y-0" : "translate-y-full"
        }`}
        data-testid="mobile-sticky-cta"
      >
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <p className="hidden min-w-0 flex-1 truncate font-display text-sm font-bold text-marine sm:block">
            {t("cta.stickyText")}
          </p>
          <Link to="/demo" className="btn-primary flex-1 sm:flex-none" data-testid="mobile-sticky-demo-button">
            {t("cta.finalPrimary")} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/tarifs" className="btn-outline shrink-0" data-testid="mobile-sticky-pricing-button">
            {t("nav.pricing")}
          </Link>
        </div>
      </div>
    </>
  );
}
